import { useState, Fragment } from 'react'
import {
  ApplicationBodyContainer,
  ApplicationBodyContent,
  DefaultTitle,
  DefaultSubtitle,
  ReviewCardContainer,
} from '../../../components/application/defaultComponents'
import ApplicationPrebuiltFooter from '../../../components/application/PrebuiltBodyFooter'
import { useApplication } from 'src/context/application'
import { useNavigate, createSearchParams, useLocation } from 'react-router-dom'
import Button from 'src/components/basic/Button'
import { IoMdCheckmark } from 'react-icons/io'
import { ReviewData } from 'src/dtos/applicationData'
import { IoIosCheckmarkCircle } from 'react-icons/io'
import StandardInput from 'src/components/basic/StandardInput'
import FilePicker from 'src/components/basic/FilePicker'
import { uploadFiles } from 'src/services/apiClient'
import { handleYesNoAnswers } from 'src/utils/formDataManipulation'
import { TbFileCheck } from 'react-icons/tb'
import AreasOfPractice from 'src/components/application/mainPagesComponents/Firm/AreasOfPractice'
import {FILE_SIZE_LABEL} from "../../../utils/constants";

const Firm = ({screenshotMode = false}) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { application, saveApplication } = useApplication()
  const [review, setReview] = useState<ReviewData>(application?.firmSection?.review ?? {})
  const [showNotesInput, setShowNotesInput] = useState(!!application?.firmSection?.review?.note)
  const [showFilePicker, setShowFilePicker] = useState(!!application?.firmSection?.review?.files)

  const areasOfPractice = application?.firmSection?.practiceAreas

  const handleEditClick = () => {
    navigate({
      pathname: '/firm/main',
      search: createSearchParams({ review: 'firm' }).toString()
    })
  }

  const updatedApplication = () => ({
    ...application,
    currentPage: window.location.pathname,
    stoppedAt: pathname,
    firmSection: {
      ...application?.firmSection,
      review
    }
  })

  const handleSaveApplication = async () => {
    try {
      await saveApplication(updatedApplication())
    } catch (_) { }
  }

  const produceApplicationData = (key: keyof ReviewData, value: any) => ({
    ...application,
    stoppedAt: pathname,
    firmSection: {
      ...application?.firmSection,
      review: {
        ...application?.firmSection?.review,
        [key]: value
      }
    }
  })

  const handleRemoveNote = async () => {
    const removedNoteApplication = produceApplicationData('note', undefined)
    setReview(prev => ({ ...prev, note: undefined }))
    setShowNotesInput(false)
    await saveApplication(removedNoteApplication)
  }

  const handleUploadFiles = async (files: File[]) => {
    if (files.length > 0) {
      const uploadedFiles = await uploadFiles(files)

      const updatedFiles = [...(review?.files ?? []), ...(uploadedFiles ?? [])]

      const applicationFileAttached = produceApplicationData('files', updatedFiles)

      setReview(prev => ({ ...prev, files: updatedFiles }))
      await saveApplication(applicationFileAttached)
    } else {
      setReview(prev => ({ ...prev, files: undefined }))
      const applicationFileAttached = produceApplicationData('files', undefined)
      await saveApplication(applicationFileAttached)
    }
  }

  const firmSection = application?.firmSection
  const transactions = application?.firmSection?.securitiesLawInfo?.transactions

  const FirmBasicsSection = () => (
    <section>
      <h5>FIRM BASICS</h5>
      <article>
        <strong>Firm Name</strong>
        <span>{firmSection?.firmName}</span>
      </article>
      <article>
        <strong>Type of Practice</strong>
        <span>{firmSection?.practiceType}</span>
      </article>
      <article>
        <strong>Firm Established</strong>
        <span>{firmSection?.establishmentDate}</span>
      </article>
      <article>
        <strong>Contact Information</strong>
        <span>{firmSection?.contactInfo?.phoneNumber}</span>
        {firmSection?.contactInfo?.website ?
          <span>{firmSection?.contactInfo?.website}</span> :
          null
        }
        {firmSection?.contactInfo?.faxNumber ?
          <span>{firmSection?.contactInfo?.faxNumber}</span> :
          null
        }
      </article>
      <article>
        <strong>Principal Mailing Address</strong>
        <span>{firmSection?.mailAddress?.formattedAddress}</span>
        <span>{firmSection?.mailAddress?.suiteNumber}</span>
      </article>
      {firmSection?.isBillingEqualToMail === false ? (
        <article>
          <strong>Billing Address</strong>
          <span>{firmSection?.billingAddress?.formattedAddress}</span>
          <span>{firmSection?.billingAddress?.suiteNumber}</span>
        </article>
      ) : null}
      {firmSection?.isSecondaryContactAdded === true ? (
        <article>
          <strong>Secondary Contact</strong>
          <span>{[firmSection?.secondaryContact?.firstName, firmSection?.secondaryContact?.lastName].join(' ')}</span>
          <span>{firmSection?.secondaryContact?.emailAddress}</span>
          <span>{firmSection?.secondaryContact?.phoneNumber}</span>
          {firmSection?.secondaryContact?.isFirmAdministrator === true ?
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <IoIosCheckmarkCircle /> Firm Administrator
            </span> :
            null
          }
        </article>
      ) : null}
      <article>
        <strong>Number of Offices</strong>
        <span>{firmSection?.numberOfOffices ?? 'N/A'}</span>
      </article>
    </section>
  )

  const AOPSection = () => (
    <section>
      {areasOfPractice && areasOfPractice.length > 0 ? (
        <Fragment>
          <h5>AREAS OF PRACTICE</h5>
          <AreasOfPractice practiceAreas={areasOfPractice} />
        </Fragment>
      ) : null}
    </section>
  )

  const RealEstateSection = () => (
    <section>
      <h5>REAL ESTATE</h5>
      <article>
        <strong>Is any firm member a licensed escrow officer for a title company?</strong>
        <span>{handleYesNoAnswers(firmSection?.realEstateInfo?.hasFirmMemberWithEscrowLicense)}</span>
      </article>
      <article>
        <strong>Does Applicant or any firm member have any agreements with title companies?</strong>
        <span>{handleYesNoAnswers(firmSection?.realEstateInfo?.hasAgreementWithTitleCompanies)}</span>
        {firmSection?.realEstateInfo?.hasAgreementWithTitleCompanies === true ? firmSection?.realEstateInfo?.agreementWithTitleCompaniesFile?.map(file =>
          <span key={file?.fileName} style={{ flexDirection: 'row', alignItems: 'center' }}><TbFileCheck /> {file?.fileName ?? 'N/A'}</span>
        ) : null}
      </article>
      <article>
        <strong>Is any firm member a fee attorney for a title company?</strong>
        <span>{handleYesNoAnswers(firmSection?.realEstateInfo?.hasFirmMemberFeeAttorney)}</span>
      </article>
      <article>
        <strong>Is any firm member an outside closing attorney, approved attorney or P-22 attorney?</strong>
        <span>{handleYesNoAnswers(firmSection?.realEstateInfo?.hasFirmMemberOutsideClosingAttorney)}</span>
      </article>
      <article>
        <strong>How many non-attorney licensed escrow officers are currently employed by your firm?</strong>
        <span>{firmSection?.realEstateInfo?.nonAttorneysWithEscrowLicenseQuantity ?? 'N/A'}</span>
      </article>
    </section>
  )

  const SecuritiesSection = () => (
    <section>
      <h5>SECURITIES LAW</h5>
      {firmSection?.securitiesLawInfo?.hasFiles === true ? (
        <>
          <article>
            <strong>
              Uploaded files of securities transactions
            </strong>
            {firmSection?.securitiesLawInfo?.securitiesLawFiles?.map(file => (
              <span key={file.fileName} style={{ flexDirection: 'row', alignItems: 'center' }}><TbFileCheck /> {file.fileName}</span>
            ))}
          </article>
        </>
      ) : (
        <>
          {transactions && transactions.length > 0 ? transactions?.map((transaction, index) =>
            <Fragment key={index}>
              <article><span><h6>Transaction {index + 1}</h6></span></article>
              <article>
                <strong>
                  Date
                </strong>
                <span>{transaction?.date ?? 'N/A'}</span>
              </article>
              <article>
                <strong>
                  Client name
                </strong>
                <span>{transaction?.clientName ?? 'N/A'}</span>
              </article>
              <article>
                <strong>
                  Size of offering
                </strong>
                <span>{transaction?.offeringSize ?? 'N/A'}</span>
              </article>
              <article>
                <strong>
                  Role in transaction
                </strong>
                <span>{transaction?.transactionRole ?? 'N/A'}</span>
              </article>
            </Fragment>
          ) : null}
        </>
      )}
    </section>
  )

  const RelationshipsSection = () => (
    <section>
      <h5>RELATIONSHIPS</h5>
      <article>
        <strong>Has the Firm ever sued a client for fees?</strong>
        <span>{handleYesNoAnswers(firmSection?.relationships?.hasSuedClientForFees)}</span>
      </article>
    </section>
  )

  const LossPreventionSection = () => {
    if (!firmSection?.relationships?.hasSuedClientForFees) {
      return null;
    }

    return (
      <section>
        <h5>LOSS PREVENTION</h5>
        <article>
          <strong>Does Applicant share office spaces or expenses with another attorney or law firm?</strong>
          <span>{handleYesNoAnswers(firmSection?.lossPrevention?.shareOfficeSpaceOrExpenses)}</span>
          {firmSection?.lossPrevention?.shareOfficeSpaceOrExpenses === true ?
            firmSection?.lossPrevention?.sharedSpaceExpensesAttorneys?.map((shared, index) => (
              <>
                <br />
                <span><h6>Shared Expense Attorney {index + 1}</h6></span>
                <span><h6>Attorney or Law Firm name:</h6>{shared?.fullName ?? 'N/A'}</span>
                <span><h6>Other Attorney Bar Number:</h6>{shared?.barCardNumber ?? 'N/A'}</span>
                <span><h6>Date sharing began:</h6>{shared?.dateSharingBegan ?? 'N/A'}</span>
              </>
            )) :
            null
          }
        </article>
        <article>
          <strong>If Applicant is a sole practitioner, who is responsible for Applicant's practice in the event of a prolonged absence?</strong>
          <span>{firmSection?.lossPrevention?.prolongedAbsenceResponsible ?? 'N/A'}</span>
        </article>
        <article>
          <strong>How does Applicant handle the scheduling of work, deadlines and appointments?</strong>
          {firmSection?.lossPrevention?.workScheduling?.map(work => (
            <span key={work}>{work ?? 'N/A'}</span>
          ))}
          {!!firmSection?.lossPrevention?.workSchedulingOther ? <span>{firmSection?.lossPrevention?.workSchedulingOther}</span> : null}
          {(!!firmSection?.lossPrevention?.workSchedulingFiles &&
            firmSection?.lossPrevention?.workSchedulingFiles.length > 0) ?
            firmSection?.lossPrevention?.workSchedulingFiles.map(file => (
              <span key={file.id} style={{ flexDirection: 'row', alignItems: 'center' }}><TbFileCheck /> {file.fileName}</span>
            )) :
            null
          }
        </article>
        <article>
          <strong>Do all firm members use engagement letters that specify scope of services, terms and billing rates?</strong>
          <span>{handleYesNoAnswers(firmSection?.lossPrevention?.engagementLetters)}</span>
        </article>
        <article>
          <strong>Do all firm members use non-engagement letter when declining a representation?</strong>
          <span>{handleYesNoAnswers(firmSection?.lossPrevention?.nonEngagementLettersWhenDeclining)}</span>
        </article>
        <article>
          <strong>Do all firm members use disengagement letter when ceasing representation?</strong>
          <span>{handleYesNoAnswers(firmSection?.lossPrevention?.disengagementLettersWhenCeasing)}</span>
        </article>
        <article>
          <strong>How does Applicant avoid conflicts of interest among and with clients?</strong>
          {firmSection?.lossPrevention?.avoidConflictOfInterests?.map(coi => (
            <span key={coi}>{coi ?? 'N/A'}</span>
          ))}
          {!!firmSection?.lossPrevention?.avoidConflictOfInterestsOther ? <span>{firmSection?.lossPrevention?.avoidConflictOfInterestsOther}</span> : null}
          {(!!firmSection?.lossPrevention?.avoidConflictOfInterestsFiles &&
            firmSection?.lossPrevention?.avoidConflictOfInterestsFiles.length > 0) ?
            firmSection?.lossPrevention?.avoidConflictOfInterestsFiles.map(file => (
              <span key={file.id} style={{ flexDirection: 'row', alignItems: 'center' }}><TbFileCheck /> {file.fileName}</span>
            )) :
            null
          }
        </article>
        <article>
          <strong>If a potential conflict of interest exists, do all firm members obtain written conflicts waivers that clearly:</strong>
          <span><h6>Advise client(s) of the nature of potential conflict? </h6></span>
          <span>{handleYesNoAnswers(firmSection?.lossPrevention?.conflictWaiversPotentialConflict)}</span>
          <span><h6>Advise client(s) how the potential conflict could affect representation? </h6></span>
          <span>{handleYesNoAnswers(firmSection?.lossPrevention?.conflictWaiversRepresentationAffected)}</span>
          <span><h6>Advise client(s) to consult another attorney about the potential conflict? </h6></span>
          <span>{handleYesNoAnswers(firmSection?.lossPrevention?.conflictWaiversAnotherAttorneyConsult)}</span>
        </article>
        <article>
          <strong>How many times in the last two years has Applicant sued a client for fees?</strong>
          <span>{firmSection?.lossPrevention?.timesSuedClientsForFees ?? 'N/A'}</span>
        </article>
        <article>
          <strong>How many clients that were sued, if any, filed a counterclaim for legal malpractice or breach of fiduciary duty?</strong>
          <span>{firmSection?.lossPrevention?.howManyClientsSuedFiledCounterClaim ?? 'N/A'}</span>
        </article>
      </section>
    );
  };

  const ColumnsBuilder = () => {
    const sectionsArray = [
      <FirmBasicsSection />,
      <AOPSection />,
      <RealEstateSection />,
      <SecuritiesSection />,
      <RelationshipsSection />,
      <LossPreventionSection />,
    ]

    const sectionsToRender = sectionsArray
      .map((section, index) => {
        switch (index) {
          case 2:
            return !!firmSection?.practiceAreas?.find(a => a.area === 'Real Estate') ? section : null
          case 3:
            return !!firmSection?.practiceAreas?.find(a => a.area === 'Securities Municipal Bonds - Tax Exempt' || a.area === 'Securities - Other Types Of Securities Practice') ? section : null
          case 6:
            return firmSection?.relationships?.hasSuedClientForFees === true ? section : null
          default: return section
        }
      })
      .filter(section => section !== null)

    const leftColumn = sectionsToRender.filter((section, index) => index % 2 === 0)
    const rightColumn = sectionsToRender.filter((section, index) => index % 2 !== 0)

    return (
      <>
        <div className='column'>
          {leftColumn}
        </div>
        <div className='column'>
          {rightColumn}
        </div>
      </>
    )
  }

  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent width='80%'>
        <DefaultTitle>Almost done! Please review your firm information before you sign.</DefaultTitle>
        <DefaultSubtitle>If any answers are innacurate or incomplete you can use the edit button to change them.</DefaultSubtitle>
        <ReviewCardContainer id='firmData'>
          <header>
            <div>
              <h4>Firm</h4>
              {!screenshotMode && (
                <h6>
                  <IoMdCheckmark />
                  Complete
                </h6>
              )}
            </div>
            {!screenshotMode && (
              <div>
                <Button style={{ padding: '8px 46px' }} onClick={handleEditClick}>Edit Section</Button>
              </div>
            )}
          </header>
          <main className='firm'>
            {ColumnsBuilder()}
          </main>
          {!screenshotMode && (
            <footer>
              {showNotesInput ? (
                <div className="review-footer-note-container">
                  <StandardInput
                    label=""
                    rows={15}
                    value={review?.note ?? ''}
                    onChange={e => setReview(prev => ({ ...prev, note: e.target.value }))}
                  />
                  <div className="review-footer-note-buttons">
                    {!!firmSection?.review?.note ? (
                      <Button
                        variant="error"
                        onClick={handleRemoveNote}
                      >
                        Remove note
                      </Button>
                    ) : (
                      <>
                        <Button
                          className="review-button-small-width"
                          onClick={handleSaveApplication}
                        >
                          Save details
                        </Button>
                        <Button
                          variant="error"
                          className="review-button-small-width"
                          onClick={() => {
                            setReview(prev => ({ ...prev, note: undefined }))
                            setShowNotesInput(false)
                          }}
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="review-footer-button-container">
                  <Button
                    variant="secondary"
                    onClick={() => setShowNotesInput(true)}
                  >
                    Add a note
                  </Button>
                </div>
              )}
              {showFilePicker ? (
                <div className='review-footer-filepicker-container'>
                  <FilePicker 
                    title={FILE_SIZE_LABEL}
                    onFilesSelect={handleUploadFiles}
                    selectedFiles={review?.files ?? []}
                    accept='.doc,.docx,application/msword, application/pdf'
                    limit={100}
                  />
                </div>
              ) : (
                <div className="review-footer-button-container">
                  <Button
                    variant="secondary"
                    onClick={() => setShowFilePicker(true)}
                  >
                    Attach a document
                  </Button>
                </div>
              )}
            </footer>
          )}
        </ReviewCardContainer>
      </ApplicationBodyContent>
      <ApplicationPrebuiltFooter
        goBackRoute="/quote/select"
        nextRoute="/review/attorneys"
        updatedApplication={updatedApplication()}
      />
    </ApplicationBodyContainer>
  )
}

export default Firm